import React from 'react'
import { Collapse, NavItem } from 'reactstrap'
import { NavLink } from 'react-router-dom'
import className from 'classnames'
import { Tooltip } from 'antd'

// Khoảng hở giữa flyout và đáy màn hình, và mép trên tối thiểu (chừa chỗ thanh điều hướng).
// Không còn hằng ƯỚC LƯỢNG chiều cao một mục: `positionFlyout` đo thẳng trên DOM.
const SUBMENU_BOTTOM_GAP = 8
const SUBMENU_MIN_TOP = 48

class SidebarItem extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            collapse: false,
            distanceToTop: null,
            // `null` = panel ôm trọn nội dung, KHÔNG đặt `max-height` ⇒ không thể có thanh cuộn.
            // Chỉ khác `null` khi danh sách con cao hơn cả màn hình.
            maxHeight: null
        }
        this.toggle = this.toggle.bind(this)
        this.myElementRef = React.createRef()
        this.handleDocumentMouseDown = this.handleDocumentMouseDown.bind(this)
    }

    componentDidMount() {
        this.loadStateFromProps()
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleDocumentMouseDown)
    }

    /**
     * Flyout của nhóm submenu là `position: fixed` phủ lên vùng nội dung (xem _sidebar.scss), nên
     * bấm ra ngoài phải đóng lại — không có listener này thì panel nằm đè trang cho tới khi người
     * dùng bấm lại tiêu đề hoặc đổi route.
     */
    handleDocumentMouseDown(event) {
        // `NavItem` của reactstrap là function component chỉ spread props lên `<li>`, không nhận
        // được ref — nên đi ngược từ ref của tiêu đề nhóm ra thẻ bọc. Thẻ bọc chứa cả flyout, nên
        // bấm bên trong danh sách con không bị tính là bấm ra ngoài.
        const anchor = this.myElementRef.current

        if (!anchor) return

        const container = anchor.closest('.nav-item-submenu')

        if (container && container.contains(event.target)) return

        this.setState({ collapse: false })
    }

    /**
     * Đặt vị trí + giới hạn chiều cao flyout theo SỐ ĐO THẬT trên DOM.
     *
     * Flyout là `position: fixed` neo bằng inline `top` (thắng CSS). Nhóm nằm càng thấp trong
     * sidebar thì `top` càng lớn; nếu cứ neo đúng vị trí nhóm thì phần đuôi danh sách con tràn
     * khỏi đáy màn hình. Nên: neo đúng vị trí nhóm khi còn chỗ, và **đẩy LÊN** vừa đủ khi không
     * còn — tức panel luôn hiện trọn, không cuộn.
     *
     * ⚠️ Đo `panel.scrollHeight` (chiều cao NỘI DUNG), không ước lượng theo số mục. Bản trước ước
     *    lượng 40px/mục, trong khi nhãn dài xuống hai dòng là ~60px — nhóm bốn mục hụt hơn 60px và
     *    panel lại mọc thanh cuộn, đúng thứ phép kẹp này sinh ra để tránh. `scrollHeight` dùng được
     *    vì CSS không còn kéo panel xuống đáy màn hình (`bottom: 0` đã bỏ, nay là `height: auto`).
     *
     * ⚠️ Phải chạy cho CẢ hai đường mở flyout. Bấm tiêu đề thì `state.collapse` bật, còn RÊ CHUỘT
     *    mở flyout thuần bằng CSS `:hover` mà `collapse` vẫn `false`. Bản trước CÓ đo trên cả hai
     *    đường (`handleMouseEnter` gọi vô điều kiện) — chỗ nó sai là phép ƯỚC LƯỢNG ở trên, không
     *    phải không chạy.
     */
    positionFlyout = () => {
        const anchor = this.myElementRef.current

        if (!anchor) return

        const container = anchor.closest('.nav-item-submenu')
        const panel = container && container.querySelector('.nav-group-sub')

        // `display: none` -> không có hộp nào để đo. Bỏ qua, sẽ đo lại lúc panel thật sự hiện.
        if (!panel || !panel.getClientRects().length) return

        const viewport = window.innerHeight
        const available = viewport - SUBMENU_MIN_TOP - SUBMENU_BOTTOM_GAP
        const contentHeight = panel.scrollHeight

        if (!contentHeight) return

        // Cao hơn cả màn hình thì đành cuộn — nhưng dán sát mép trên để tận dụng tối đa chỗ trống.
        const fits = contentHeight <= available
        const distanceToTop = fits
            ? Math.min(
                  Math.max(anchor.getBoundingClientRect().top, SUBMENU_MIN_TOP),
                  viewport - SUBMENU_BOTTOM_GAP - contentHeight
              )
            : SUBMENU_MIN_TOP
        const maxHeight = fits ? null : available

        // So trước khi ghi: `componentDidUpdate` gọi lại hàm này sau mỗi lần setState, không chặn là
        // vòng lặp vô hạn.
        if (distanceToTop !== this.state.distanceToTop || maxHeight !== this.state.maxHeight) {
            this.setState({distanceToTop, maxHeight})
        }
    }

    handleMouseEnter() {
        // Đo ngay (CSS `:hover` đã hiện panel trước khi React nhận sự kiện) và đo lại ở khung hình
        // kế — phòng trình duyệt chưa kịp dàn lại bố cục.
        this.positionFlyout()
        window.requestAnimationFrame(this.positionFlyout)
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (!this.props.isOpenParent && prevProps.isOpenParent) {
            this.setState({ collapse: false })
        }

        // Đo lại sau mỗi lần render (đổi số mục, mở/đóng...). Hàm tự bỏ qua khi panel đang ẩn và
        // tự chặn ghi khi kết quả không đổi, nên gọi vô điều kiện là an toàn.
        if (this.props.data && this.props.data.items) {
            this.positionFlyout()
        }

        // Bấm vào một mục con thì đóng flyout lại, không để nó che trang vừa mở
        const {location} = this.props
        const prevLocation = prevProps.location

        if (this.state.collapse && !!location && !!prevLocation && location.pathname !== prevLocation.pathname) {
            this.setState({ collapse: false })
        }

        // Chỉ nghe click ra ngoài trong lúc flyout đang mở
        const {data} = this.props

        if (data && data.items && this.state.collapse !== prevState.collapse) {
            if (this.state.collapse) {
                document.addEventListener('mousedown', this.handleDocumentMouseDown)
            } else {
                document.removeEventListener('mousedown', this.handleDocumentMouseDown)
            }
        }
    }

    loadStateFromProps = () => {
        const {data} = this.props

        // Nhóm submenu hiện dạng flyout phủ lên nội dung (xem _sidebar.scss), nên chỉ mở khi
        // người dùng bấm — không tự bung theo route đang đứng để khỏi che mất trang vừa vào.
        if (data && data.items) return

        this.setState({
            collapse: this.isOpenCollapse(data)
        })
    }

    toggle() {
        this.setState(state => ({ collapse: !state.collapse }))
    }

    activeRoute = (route) => {
        if (!route.path || typeof route.path !== 'string') return false

        const trimRightPath = route.path.trim().replace(/\/+$/gm, '')
        const currentPath = this.props.location.pathname

        if (route.exact || trimRightPath.length === currentPath.length) {
            return trimRightPath === currentPath
        }

        return currentPath.startsWith(trimRightPath + '/')
    }

    isOpenCollapse = (routes) => {
        if (!routes.items) return this.activeRoute(routes)
        const itemActive = routes.items.find(item => this.isOpenCollapse(item))

        return !!itemActive
    }

    renderItemHeader = (data) => {
        return (
            <li className="nav-item-header">
                <div className="text-uppercase font-size-xs line-height-xs">{data.name}</div>
                <i className="icon-menu" title={data.name} />
            </li>
        )
    }

    renderItemComponent = (data, total, totalTooltip) => {
        return (
            <NavItem>
                {
                    data.name !== 'Thống kê item lỗi QC' && data.name !== 'Statics Overview' && data.name !== 'Báo cáo chi tiết' && this.props.isCollapsed ?
                        <>
                            <Tooltip placement="right" title={data.name}>
                                <NavLink to={data.path} className='nav-link' exact={data.exact}>
                                    {data.icon}
                                    <span>{data.name}</span>
                                </NavLink>
                            </Tooltip>
                            {!!total && (totalTooltip ?
                                <Tooltip placement={'right'} title={totalTooltip}>
                                    <span className='NumberPending'>{total}</span>
                                </Tooltip> :
                                <span className='NumberPending'>{total}</span>
                            )}
                        </> :
                        <NavLink to={data.path} className='nav-link' exact={data.exact}>
                            {data.icon}
                            <span>{data.name}</span>
                            {!!total && (totalTooltip ?
                                <Tooltip placement={'right'} title={totalTooltip}>
                                    <span className='NumberPending'>{total}</span>
                                </Tooltip> :
                                <span className='NumberPending'>{total}</span>
                            )}
                        </NavLink>
                }
            </NavItem>
        )
    }

    render() {
        const { data, total, isCollapsed, totalTooltip, ...rest } = this.props
        if (data.component) return this.renderItemComponent(data, total, totalTooltip)
        if (data.header) return this.renderItemHeader(data)
        if (data.items) {
            // Nhóm không có path riêng (Report, KPI hiệu suất) thì tiêu đề chỉ để mở danh sách
            // con, không điều hướng — dùng div thay NavLink để bấm không đẩy history.
            // ⚠️ `myElementRef` phải gắn ở CẢ hai nhánh. Hôm nay mọi nhóm đều `path: ''` nên nhánh
            //    `NavLink` chưa chạy, nhưng ngày ai đó cho một nhóm path riêng thì thiếu ref khiến
            //    `positionFlyout` thoát ở `if (!anchor) return` (flyout mất phép kẹp vị trí,
            //    quay lại cảnh tràn đáy màn hình) và `handleDocumentMouseDown` cũng thoát — flyout
            //    `position: fixed` phủ vùng nội dung sẽ KHÔNG BAO GIỜ đóng khi bấm ra ngoài.
            //
            //    Dùng `ref`, KHÔNG dùng `innerRef`: antd `Tooltip` bọc ngoài đã gán `ref` cho child
            //    (`rc-trigger` → `composeRef`), mà `react-router-dom@5.2` làm
            //    `props.ref = forwardedRef || innerRef` — nên `innerRef` bị bỏ qua và ref không bao
            //    giờ chạy. Đo ở vòng review 15.
            const hasOwnPath = !!data.path && typeof data.path === 'string' && !!data.path.trim()

            // Sáng tên nhóm khi đang đứng ở một trong các mục con của nó
            const isActive = data.items.some(item => this.isOpenCollapse(item))

            const submenuClasses = className({
                'nav-item-submenu': true,
                // Bấm tiêu đề nhóm thì giữ flyout mở, không cần rê chuột (xem layout/_sidebar.scss)
                'nav-item-submenu-open': this.state.collapse,
            })
            return <NavItem className={submenuClasses} onMouseEnter={() => this.handleMouseEnter()}>
                {
                    hasOwnPath ?
                        <Tooltip placement="top" title={isCollapsed ? data.name : ""}>
                            <NavLink className='nav-link' to={data.path} ref={this.myElementRef}
                                     onClick={this.toggle}>
                                {data.icon}
                                <span>{data.name}</span>
                            </NavLink>
                        </Tooltip>
                        :
                        <Tooltip placement="top" title={isCollapsed ? data.name : ""}>
                            <div className={`nav-link cursor-pointer ${isActive ? 'active' : ''}`}
                                 ref={this.myElementRef} onClick={this.toggle}>
                                {data.icon}
                                <span>{data.name}</span>
                            </div>
                        </Tooltip>
                }
                {/*
                  * `top` và `maxHeight` đều do `positionFlyout` tính từ số đo thật — CSS không biết
                  * chiều cao nội dung nên không tự làm được. `maxHeight` chỉ khác `null` khi danh
                  * sách con cao hơn cả màn hình; còn lại panel ôm trọn nội dung nên KHÔNG có thanh
                  * cuộn. Truyền `undefined` (không phải `null`) để React bỏ hẳn thuộc tính.
                  */}
                <Collapse
                    isOpen={this.state.collapse}
                    tag='ul'
                    className='nav nav-group-sub'
                    style={{
                        top: this.state.distanceToTop,
                        maxHeight: this.state.maxHeight === null ? undefined : this.state.maxHeight,
                    }}
                >
                    {
                        data.items.filter(item => !item.hidden).map((item, key) => {
                            return <SidebarItem {...rest} key={key} data={item} isOpenParent={this.state.collapse} />
                        })
                    }
                </Collapse>
            </NavItem>
        }

        return null
    }
}

export default SidebarItem